import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Divider,
    Input,
    InputNumber,
    List,
    message,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Typography
} from 'antd';
import { collection, doc, getDoc, getDocs, query, where, writeBatch, updateDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import { useEventBookingData } from '../../hooks/useEventBookingData';
import usePaginatedRegistrants from '../../hooks/useFetchRegistrantsData';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';

const { Title, Text, Paragraph } = Typography;


/**
 * Generates and uploads the full seat availability for all sessions of an event.
 * WARNING: This will CREATE OR OVERWRITE all seat data for the specified event and sessions.
 * @param {string} eventId - The ID of the event.
 * @param {Array<string>} sessions - An array of session identifiers (e.g., "2025-08-25_09:00-10:00").
 */
export const uploadFullSeatLayout = async (eventId, venueId, sessions) => {
    console.log(`Starting to generate seat availability for event: ${eventId}`);
    if (!sessions || sessions.length === 0) {
        throw new Error('No sessions provided to generate seats for.');
    }

    try {
        // 1. Get the physical layout of the venue
        let seatTemplates = [];
        const eventDocRef = doc(db, 'events', eventId);
        const eventSnap = await getDoc(eventDocRef);
        
        if (eventSnap.exists()) {
            const eventData = eventSnap.data();
            if (eventData.competitionScheduleState?.status === 'draft') {
                throw new Error('Publish the competition schedule before generating numbered seats.');
            }
            const venue = (eventData.venues || []).find(v => v.id === venueId);
            if (venue && venue.seatConfig && venue.seatConfig.length > 0) {
                // Generate templates based on dynamic venue config
                venue.seatConfig.forEach(config => {
                    const { row, areaType, seatCount } = config;
                    for (let number = 1; number <= seatCount; number++) {
                        seatTemplates.push({
                            seatLabel: `${row}${number}`,
                            areaType,
                            row,
                            number,
                        });
                    }
                });
            }
        }
        
        // Throw an error if no dynamic config exists
        if (seatTemplates.length === 0) {
            throw new Error(`No seat configuration found for venueId: ${venueId}`);
        }

        const allSeatInstances = [];

        // 1.5 Fetch all existing seats for these sessions to preserve booked/locked seats
        const existingSeatsMap = new Map();
        for (const sessionId of sessions) {
            const seatsRef = collection(db, `seats${eventId}`);
            const q = query(seatsRef, where('sessionId', '==', sessionId), where('venueId', '==', venueId));
            const snapshot = await getDocs(q);
            snapshot.forEach(docSnap => {
                existingSeatsMap.set(docSnap.id, docSnap.data());
            });
        }

        // 2. Create a unique document for EACH seat in EACH session
        for (const sessionId of sessions) {
            for (const seatTemplate of seatTemplates) {
                const documentId = `${venueId}-${seatTemplate.areaType}-${seatTemplate.seatLabel}_${eventId}_${sessionId}`;
                const oldDocumentId = `${seatTemplate.areaType}-${seatTemplate.seatLabel}_${eventId}_${sessionId}`;
                
                const existingSeat = existingSeatsMap.get(documentId) || existingSeatsMap.get(oldDocumentId);

                if (existingSeat && existingSeat.status !== 'available') {
                    // Preserve the seat exactly as it is (locked or booked)
                    allSeatInstances.push({
                        ...existingSeat,
                        id: existingSeatsMap.has(oldDocumentId) ? oldDocumentId : documentId
                    });
                } else {
                    allSeatInstances.push({
                        id: existingSeatsMap.has(oldDocumentId) ? oldDocumentId : documentId, // For the batch operation
                        eventId: eventId,
                        venueId, // Add the venueId to each document
                        sessionId: sessionId,
                        status: 'available',
                        ...seatTemplate
                    });
                }
            }
        }
        console.log(`Generated a total of ${allSeatInstances.length} seat instances across ${sessions.length} sessions.`);

        // 3. Use Batched Writes to upload the data efficiently
        const batchSize = 499;
        for (let i = 0; i < allSeatInstances.length; i += batchSize) {
            const batch = writeBatch(db);
            const chunk = allSeatInstances.slice(i, i + batchSize);

            console.log(`Preparing batch ${Math.floor(i / batchSize) + 1}...`);
            chunk.forEach(seat => {
                const docRef = doc(db, `seats${eventId}`, seat.id);
                batch.set(docRef, seat);
            });

            console.log("Committing batch...");
            await batch.commit();
        }

        console.log("✅ Successfully uploaded all seat instances to Firestore!");

        // 4. Update the events document to flag these sessions as generated
        const updates = {};
        sessions.forEach(sessionId => {
            updates[`sessionsSeatsGenerated.${sessionId}`] = true; // Legacy support
            updates[`sessionsSeatsGenerated.${venueId}_${sessionId}`] = true;
        });
        await updateDoc(eventDocRef, updates);
        console.log("✅ Marked sessions as generated in events document.");

    } catch (error) {
        console.error("❌ Error uploading seat layout:", error);
        throw error;
    }
};

const SeatingEvent = () => {
    const { currentEventId } = useAuth();
    const eventId = currentEventId;

    // --- Data Fetching Hooks ---
    const { registrantDatas, loading: registrantsLoading } = usePaginatedRegistrants(9999, "Registrants2025", "createdAt");
    const { event, setLoading, loading: eventLoading, error } = useEventBookingData(eventId);

    // --- NEW STATE for the assignment process ---
    const [seatToAssign, setSeatToAssign] = useState(null); // Stores the seat that was clicked
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false); // Controls the registrant modal
    const [registrantToAssign, setRegistrantToAssign] = useState(null); // Stores the registrant selected in the modal

    // --- UI State (for modal, search, etc.) ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tempSelectedRow, setTempSelectedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [seatLayout, setSeatLayout] = useState([]); // This will hold the single, flat list from the backend
    const [isSeatMapLoading, setIsSeatMapLoading] = useState(false);

    const [useTestEmail, setUseTestEmail] = useState(false);

    // --- NEW STATE for Modal and Submission ---
    const [modalMode, setModalMode] = useState('select'); // 'select' or 'manual'
    const [manualName, setManualName] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [submitType, setSubmitType] = useState('standard'); // 'standard' or 'general'

    // --- React Hook Form Initialization ---
    const { control, handleSubmit, watch, setValue } = useForm({
        defaultValues: {
            registrant: null,
            venue: undefined, // Use undefined for antd placeholder to show
            date: null,
            session: null,
            addOns: [],
            tickets: [
                { id: 'presto', name: 'Presto', basePrice: event?.baseTicketPrice || 0, quantity: 0, wantsSeat: false, seatQuantity: 0 },
                { id: 'allegro', name: 'Allegro', basePrice: event?.baseTicketPrice || 0, quantity: 0, wantsSeat: false, seatQuantity: 0 },
                { id: 'lento', name: 'Lento', basePrice: event?.baseTicketPrice || 0, quantity: 0, wantsSeat: false, seatQuantity: 0 }
            ]
        }
    });

    const { fields } = useFieldArray({ control, name: "tickets" });
    const watchedFormData = watch(); // Watch all form data for reactive UI updates

    // --- Modal Logic ---
    const showModal = () => setIsModalOpen(true);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTempSelectedRow(null);
        setManualName('');
        setManualEmail('');
        setModalMode('select'); // Reset mode on close
    };

    const handleModalConfirm = () => {
        if (modalMode === 'select') {
            if (tempSelectedRow) {
                const performer = tempSelectedRow.performers[0];
                setValue('registrant', {
                    id: tempSelectedRow.id,
                    name: performer.fullName ? `${performer.fullName}`.toLowerCase() : `${performer.firstName} ${performer.lastName}`,
                    email: performer.email
                }, { shouldValidate: true });
                handleCloseModal();
            } else {
                message.error("Please select a registrant from the list.");
            }
        } else {
            // Manual mode
            if (!manualName || !manualEmail) {
                message.error("Please enter both a name and an email.");
                return;
            }
            // Create a "mock" registrant object.
            // The ID 'manual' will be checked in the submit function.
            setValue('registrant', {
                id: 'manual',
                name: manualName,
                email: manualEmail
            }, { shouldValidate: true });
            handleCloseModal();
        }
    };

    const filteredData = useMemo(() => {
        if (!registrantDatas) return [];
        if (!searchTerm) return registrantDatas;
        return registrantDatas.filter(registrant => {
            const performer = registrant.performers?.[0];
            if (!performer) return false;
            const fullName = performer.fullName ? `${performer.fullName}`.toLowerCase() : `${performer.firstName} ${performer.lastName}`.toLowerCase();
            return fullName.includes(searchTerm.toLowerCase());
        });
    }, [registrantDatas, searchTerm]);

    const dynamicVenueOptions = useMemo(() => {
        if (!event || !event.venues) return [];
        return event.venues.map(v => ({ value: v.id, label: v.label }));
    }, [event]);

    useEffect(() => {
        const fetchSeatMap = async () => {
            if (watchedFormData.venue && watchedFormData.date && watchedFormData.session) {
                setIsSeatMapLoading(true);
                try {
                    const sessionId = `${watchedFormData.date}_${watchedFormData.session}`;
                    const q = query(
                        collection(db, `seats${eventId}`),
                        where('venueId', '==', watchedFormData.venue),
                        where('sessionId', '==', sessionId)
                    );
                    const querySnapshot = await getDocs(q);
                    const seats = querySnapshot.docs.map(doc => doc.data());
                    setSeatLayout(seats);
                } catch (err) {
                    console.error("Failed to fetch seat map:", err);
                    message.error("Could not load the seat map for this session.");
                } finally {
                    setIsSeatMapLoading(false);
                }
            } else {
                // If venue/date/session is deselected, clear the map
                setSeatLayout([]);
            }
        };

        fetchSeatMap();
    }, [watchedFormData.venue, watchedFormData.date, watchedFormData.session]);

    const formattedSessionLayout = useMemo(() => {
        if (!seatLayout || seatLayout.length === 0) return {};
        const seatsForCorrectVenue = seatLayout.filter(seat => seat.venueId === watchedFormData.venue);


        // 1. Group all seats by their area (lento, allegro, presto)
        const groupedByArea = seatsForCorrectVenue.reduce((acc, seat) => {
            const area = seat.areaType;
            if (!acc[area]) acc[area] = [];
            acc[area].push(seat);
            return acc;
        }, {});

        const finalLayouts = {};

        // --- THIS IS THE NEW SORTING LOGIC ---
        // 1. Define your desired custom sort order.
        const areaOrder = ['presto', 'allegro', 'lento'];

        const rightSideMax = {
            A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
            G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
            N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
        };

        // 2. Loop through your custom order array instead of the object keys.
        areaOrder.forEach(area => {
            // Make sure the area actually exists in your data before processing it
            if (groupedByArea[area]) {
                const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                    const row = seat.row;
                    if (!acc[row]) acc[row] = [];
                    acc[row].push(seat);
                    return acc;
                }, {});

                const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                finalLayouts[area] = sortedRowKeys.map(rowKey => {
                    const rowSeats = rowsObject[rowKey];
                    // Sort descending to display highest numbers on the left
                    rowSeats.sort((a, b) => b.number - a.number);
                    
                    const mappedRow = rowSeats.map(seat => ({
                        id: seat.id,
                        number: seat.number,
                        row: seat.row, // Pass the row letter for custom labels
                        isReserved: seat.status !== 'available',
                        status: seat.status,
                        // --- NEW TOOLTIP LOGIC ---
                        tooltip: seat.status === 'reserved'
                            ? `Reserved for: ${seat.assignedTo?.registrantName || 'N/A'}`
                            : `Seat ${seat.seatLabel} - Available`,
                    }));

                    if (watchedFormData.venue === 'Venue1') {
                        const splitSeatNumber = rightSideMax[rowKey];
                        if (splitSeatNumber) {
                            const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                            if (splitIndex !== -1) {
                                // Insert two nulls to create a wide aisle
                                mappedRow.splice(splitIndex, 0, null, null);
                            }
                        }
                    }

                    return mappedRow;
                });
            }
        });

        if (!seatLayout || seatLayout.length === 0) return {};
        return finalLayouts;
    }, [seatLayout, watchedFormData.venue]);

    // --- Form Submission ---
    const onFormSubmit = async (formData) => {
        if (!formData.registrant) {
            message.error('Please select or enter a registrant.');
            return;
        }

        const sanitizedTickets = formData.tickets.map(ticket => {
            if (!ticket.wantsSeat) {
                return { ...ticket, seatQuantity: 0 };
            }
            return ticket;
        });

        // Build the payload, checking if the registrant was manually entered
        const bookingPayload = {
            eventId: eventId,
            userId: formData.registrant.id === 'manual' ? 'MANUAL_ENTRY' : formData.registrant.id,
            userName: formData.registrant.name,
            userEmail: useTestEmail ? 'hello@apcsmusic.com' : formData.registrant.email,
            venue: formData.venue,
            date: formData.date,
            session: formData.session,
            tickets: sanitizedTickets.filter(t => t.quantity > 0),
            addOns: formData.addOns,
            isGeneralTicket: submitType === 'general', // <-- HERE IS THE NEW FLAG
        };

        try {
            message.loading({ content: 'Initiating your booking...', key: 'booking' });
            const saveResponse = await apis.bookings.saveSeatBookProfileInfo(bookingPayload);
            const { bookingId, seatSelectionToken } = saveResponse.data;

            const emailPayload = {
                ...bookingPayload,
                bookingId: bookingId,
                seatSelectionToken: seatSelectionToken
            };

            await apis.bookings.sendSeatBookingEmail(emailPayload);
            message.success({ content: 'Booking initiated!', key: 'booking' });
            toast.info("Please check your email to select your seat(s).");
        } catch (err) {
            message.error({ content: err.response?.data?.message || 'Failed to create booking.', key: 'booking' });
        }
    };

    const handleTriggerSubmit = (type) => {
        setSubmitType(type);
        handleSubmit(onFormSubmit)(); // This triggers react-hook-form's validation and submit
    };

    // --- Order Summary Calculation ---
    const orderSummary = useMemo(() => {
        const items = [];
        let total = 0;

        watchedFormData.tickets?.forEach(ticket => {
            if (ticket.quantity > 0) {
                items.push({ description: `${ticket.name} Ticket`, quantity: ticket.quantity, price: ticket.basePrice * ticket.quantity });
                total += ticket.basePrice * ticket.quantity;
            }
            if (ticket.wantsSeat && ticket.seatQuantity > 0) {
                const seatPrice = 0; // Example add-on price
                items.push({ description: `Seat Selection (${ticket.name})`, quantity: ticket.seatQuantity, price: seatPrice * ticket.seatQuantity });
                total += seatPrice * ticket.seatQuantity;
            }
        });

        event?.addOns?.forEach(addOn => {
            if (watchedFormData.addOns?.includes(addOn.id)) {
                items.push({ description: addOn.name, quantity: 1, price: addOn.price });
                total += addOn.price;
            }
        });

        return { items, total };
    }, [watchedFormData, event]);

    const handleUploadClick = async () => {
        setLoading(true);
        message.info('Starting full seat layout upload for all venues and sessions...');

        const generateEventSessionsArray = (venueSessions) => {
            if (!venueSessions) return [];
            const sessionsArray = [];
            Object.keys(venueSessions).forEach(date => {
                venueSessions[date].forEach(time => {
                    sessionsArray.push(`${date}_${time}`);
                });
            });
            return sessionsArray;
        };

        if (!event?.venues || event.venues.length === 0) {
            message.error('No venues found in the event configuration.');
            setLoading(false);
            return;
        }

        try {
            let totalSessionsGenerated = 0;
            for (const venue of event.venues) {
                const sessionsArray = generateEventSessionsArray(venue.sessions);
                if (sessionsArray.length > 0) {
                    await uploadFullSeatLayout(currentEventId, venue.id, sessionsArray);
                    totalSessionsGenerated += sessionsArray.length;
                }
            }
            if (totalSessionsGenerated > 0) message.success('All layouts uploaded successfully!');
            else message.error('No sessions found to generate layouts for.');
        } catch (error) {
            message.error(error.message || 'Seat generation failed.');
        } finally {
            setLoading(false);
        }
    };

    const availableSession = useMemo(() => {
        return event?.venues?.find(v => v.id === watchedFormData.venue)?.sessions || {};
    }, [watchedFormData.venue, event]);

    // --- Loading and Error States ---
    if (eventLoading || registrantsLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }
    if (error) {
        return <Alert message="Error" description="Could not load event data." type="error" showIcon />;
    }

    return (
        <form>
            <Row gutter={[32, 32]} style={{ padding: '40px' }}>
                <Col xs={24} md={14}>
                    <Title level={2}>{event.title}</Title>
                    <Paragraph>{new Date(event.date?.seconds * 1000).toLocaleDateString('en-GB', { /* ... */ })}</Paragraph>
                    <Divider />

                    <Card title="Admin Database Tools" style={{ margin: '40px' }}>
                        <p>
                            Use this tool to generate and upload the complete seat layout for an event.
                            <strong>Warning:</strong> This will overwrite any existing seat data for the event.
                        </p>
                        <Button
                            type="primary"
                            onClick={handleUploadClick}
                        >
                            Generate & Upload Full Seat Layout
                        </Button>
                    </Card>

                    {/* --- Card 1: Registrant Selection --- */}
                    <Card title="1. Select Registrant" style={{ marginBottom: '24px' }}>
                        <Row align="middle" justify="space-between">
                            <Text strong>
                                {watchedFormData.registrant ? `Selected: ${watchedFormData.registrant.name}` : 'No Registrant Selected'}
                            </Text>
                            <Button type="primary" onClick={showModal}>Select Registrant</Button>
                        </Row>
                        <Input
                            placeholder="Registrant email will appear here"
                            value={watchedFormData.registrant?.email || ''}
                            size="large"
                            disabled
                            style={{ marginTop: 16 }}
                        />
                        <Checkbox
                            checked={useTestEmail}
                            onChange={(e) => setUseTestEmail(e.target.checked)}
                            style={{ marginTop: '16px' }}
                        >
                            Use test email (hello@apcsmusic.com)
                        </Checkbox>
                    </Card>

                    {/* --- Card 2: Venue Selection --- */}
                    <Card title="2. Select Venue" style={{ marginBottom: '24px' }}>
                        <Controller
                            name="venue"
                            control={control}
                            rules={{ required: 'Please select a venue' }}
                            render={({ field, fieldState: { error } }) => (
                                <>
                                    <Select {...field} showSearch placeholder="Search to Select a Venue" style={{ width: '100%' }} options={dynamicVenueOptions} status={error ? 'error' : ''} />
                                    {error && <Text type="danger">{error.message}</Text>}
                                </>
                            )}
                        />
                    </Card>

                    {/* --- Card 3: Ticket Selection --- */}
                    <Card title="3. Select Your Tickets" style={{ marginBottom: '24px' }}>
                        {fields.map((field, index) => {
                            const currentQuantity = watchedFormData.tickets[index]?.quantity || 0;
                            return (
                                <div key={field.id} style={{ marginBottom: index === fields.length - 1 ? 0 : '16px' }}>
                                    <Row align="middle" justify="space-between">
                                        <Col><Text>{field.name} (Base Price: Rp{field.basePrice})</Text></Col>
                                        <Col><Controller name={`tickets.${index}.quantity`} control={control} render={({ field }) => <InputNumber {...field} min={0} max={10} />} /></Col>
                                    </Row>
                                    {currentQuantity > 0 && (
                                        <Row align="middle" justify="space-between" style={{ marginTop: '8px', paddingLeft: '16px' }}>
                                            <Controller name={`tickets.${index}.wantsSeat`} control={control} render={({ field }) => <Checkbox {...field} checked={field.value}>Choose specific seat (add-on)</Checkbox>} />
                                            {watchedFormData.tickets[index]?.wantsSeat && (
                                                <Controller name={`tickets.${index}.seatQuantity`} control={control} render={({ field }) => <InputNumber {...field} min={0} max={currentQuantity} placeholder="Seats" />} />
                                            )}
                                        </Row>
                                    )}
                                </div>
                            );
                        })}
                    </Card>

                    {/* --- Card 4: Date & Session --- */}
                    <Card title="4. Select Date & Session" style={{ marginBottom: '24px' }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <Radio.Group {...field} optionType="button" buttonStyle="solid" onChange={(e) => { field.onChange(e); setValue('session', null); }}>
                                        {Object.keys(availableSession).map(date => <Radio.Button key={date} value={date}>{new Date(date).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short' })}</Radio.Button>)}
                                    </Radio.Group>
                                )}
                            />
                            {watchedFormData.date && (
                                <Controller
                                    name="session"
                                    control={control}
                                    render={({ field }) => (
                                        <Radio.Group {...field} optionType="button">
                                            {availableSession[watchedFormData.date].map(session => <Radio.Button key={session} value={session}>{session}</Radio.Button>)}
                                        </Radio.Group>
                                    )}
                                />
                            )}
                        </Space>
                    </Card>

                    {watchedFormData.session && (
                        <Card title={`Live Seat Map for ${watchedFormData.session}`} style={{ marginTop: '24px' }}>
                            {isSeatMapLoading ? (
                                <div style={{ textAlign: 'center' }}><Spin /></div>
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {Object.keys(formattedSessionLayout).map(areaType => {
                                        const layoutForPicker = formattedSessionLayout[areaType] || [];
                                        return (
                                            <div key={areaType}>
                                                <Title level={5} style={{ textTransform: 'capitalize' }}>{areaType} Section</Title>
                                                <div style={{ overflowX: 'auto', padding: '10px', background: '#fafafa', borderRadius: '8px' }}>
                                                    <CustomSeatPicker
                                                        layout={layoutForPicker}
                                                        isReadOnly={true} // Set to read-only mode
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Space>
                            )}
                        </Card>
                    )}

                    {/* --- Card 5: Optional Packages --- */}
                    <Card title="5. Optional Packages" style={{ marginBottom: '24px' }}>
                        <Controller
                            name="addOns"
                            control={control}
                            render={({ field }) => (
                                <Checkbox.Group {...field} style={{ display: 'flex', flexDirection: 'column' }}>
                                    {event.addOns?.map(addOn => (
                                        <Checkbox key={addOn.id} value={addOn.id} style={{ marginBottom: '8px' }}>
                                            {addOn.name} (+Rp {addOn.price})
                                        </Checkbox>
                                    ))}
                                </Checkbox.Group>
                            )}
                        />
                    </Card>
                </Col>

                {/* --- Order Summary Column --- */}
                <Col xs={24} md={10}>
                    <Card style={{ position: 'sticky', top: '20px' }}>
                        <Title level={4}>Order Summary</Title>
                        <div style={{ marginBottom: '16px' }}>
                            {watchedFormData.registrant && <Paragraph>For: <Text strong>{watchedFormData.registrant.name}</Text></Paragraph>}
                            {watchedFormData.venue && <Paragraph>Venue: <Text strong>{dynamicVenueOptions.find(v => v.value === watchedFormData.venue)?.label}</Text></Paragraph>}
                            {watchedFormData.date && watchedFormData.session && <Paragraph>When: <Text strong>{new Date(watchedFormData.date).toLocaleDateString('id-ID')} at {watchedFormData.session}</Text></Paragraph>}
                        </div>
                        {orderSummary.items.length > 0 && <Divider style={{ margin: '0 0 16px 0' }} />}
                        <List
                            dataSource={orderSummary.items}
                            renderItem={item => (
                                <List.Item style={{ padding: '8px 0' }}>
                                    <List.Item.Meta title={item.description} description={item.quantity > 1 ? `Quantity: ${item.quantity}` : null} />
                                    <Text>Rp {item.price}</Text>
                                </List.Item>
                            )}
                        />
                        <Divider />
                        <Row justify="space-between">
                            <Col><Title level={3}>Total</Title></Col>
                            <Col><Title level={3}>Rp {orderSummary.total}</Title></Col>
                        </Row>
                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={() => handleTriggerSubmit('standard')}
                        >
                            Send Email Link
                        </Button>
                        {/* <Button
                            size="large"
                            block
                            onClick={() => handleTriggerSubmit('general')}
                            style={{ marginTop: 16 }}
                        >
                            Send Email Link (General People)
                        </Button> */}
                    </Card>
                </Col>
            </Row>

            {/* --- Registrant Selection Modal --- */}
            <Modal
                title="Select a Registrant"
                open={isModalOpen}
                onOk={handleModalConfirm}
                onCancel={handleCloseModal}
                width={1000}
                okText={modalMode === 'select' ? "Select" : "Add Registrant"}
                okButtonProps={{
                    disabled: modalMode === 'select' && !tempSelectedRow
                }}
            >
                <Radio.Group
                    onChange={(e) => setModalMode(e.target.value)}
                    value={modalMode}
                    style={{ marginBottom: 16 }}
                >
                    <Radio.Button value="select">Select from List</Radio.Button>
                    <Radio.Button value="manual">Enter Manually</Radio.Button>
                </Radio.Group>

                <Divider />

                {modalMode === 'select' && (
                    <>
                        <Input.Search
                            placeholder="Search by performer name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ marginBottom: 16 }}
                            allowClear
                        />
                        <Table
                            rowSelection={{
                                type: 'radio',
                                onChange: (_, selectedRows) => setTempSelectedRow(selectedRows[0]),
                            }}
                            columns={[
                                {
                                    title: 'Performer',
                                    key: 'performer',
                                    render: (_, rec) => {
                                        const p = rec?.performers?.[0];
                                        return p?.fullName || `${p?.firstName} ${p?.lastName}`;
                                    }
                                },
                                { title: 'Email', key: 'email', render: (_, rec) => rec?.performers[0]?.email },
                                { title: 'Instrument Category', key: 'instrumentCategory', render: (_, rec) => rec?.instrumentCategory },
                            ]}
                            dataSource={filteredData.map(item => ({ ...item, key: item.id }))}
                            pagination={{ pageSize: 5 }}
                        />
                    </>
                )}

                {modalMode === 'manual' && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input
                            placeholder="Full Name"
                            size="large"
                            value={manualName}
                            onChange={(e) => setManualName(e.target.value)}
                        />
                        <Input
                            placeholder="Email Address"
                            size="large"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                        />
                    </Space>
                )}
            </Modal>
        </form>
    );
};

export default SeatingEvent;
